import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Tooltip } from "react-tippy";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import JsBarcode from "jsbarcode";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
// import AddSingleDialog from "./AddSingleDialog";
import styles from "./main.module.scss";
import { handlePostRequest } from "../../../services/PostTemplate";
import { handleGetRequest } from "../../../services/GetTemplate";
import { handlePutRequest } from "../../../services/PutTemplate";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import AddSingleDialog from "./AddSingleDialog";
import AddMultipleDialog from "./AddMulitpleDialog";
import EditOCRPackage from "./EditOCRPackage";

const OCRPackages = () => {
    const [pendingPackages, setPendingPackages] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isMultipleOpen, setIsMultipleOpen] = useState(false);
    const [isSingleOpen, setIsSingleOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [editingPackage, setEditingPackage] = useState(null);
    const dispatch = useDispatch();

    const fetchPendingPackages = async () => {
        try {
            setIsLoading(true);
            let endpoint = "/ocr-packages";

            const response = await handleGetRequest(endpoint);
            setPendingPackages(response?.data || []);
        } catch (error) {
            console.error("Error fetching packages:", error);
            toast.error("Failed to load pending packages");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPendingPackages();
    }, []);

    const handleDeletePackage = async (packageData) => {
        try {
            setIsLoading(true);
            const response = await dispatch(handlePostRequest({}, `/ocr-packages/delete/${packageData._id}`, true, true));

            if (response !== "error") {
                toast.success("Package deleted successfully");
                fetchPendingPackages();
            }
        } catch (error) {
            console.error("Delete error:", error);
            toast.error("Failed to delete package");
        } finally {
            setIsLoading(false);
        }
    };

    const handleConvertToPackage = async (packageData) => {
        if (!packageData) {
            toast.error("Please select a package");
            return;
        }

        try {
            setIsLoading(true);

            // Move to pending first
            await dispatch(handlePostRequest({ id: packageData._id }, "/shiftOCRpackage"));

            // Get latest package info
            const lastPackage = await handleGetRequest("/getLastPackage");
            const lastWaybill = lastPackage?.data?.Waybill || 1000;
            const lastFlight = lastPackage?.data?.flightNumber || "UNKNOWN";

            // Format current date
            const currentDate = new Date();
            const formattedDate = `${currentDate.getDate().toString().padStart(2, "0")}-${(currentDate.getMonth() + 1).toString().padStart(2, "0")}-${currentDate.getFullYear()}`;

            // Prepare new package data
            const newPackage = {
                ...packageData,
                Waybill: lastWaybill + 1,
                flightNumber: lastFlight,
                shifted: true,
                name: packageData.billTo,
                billTo: packageData.packageName,
                numberOfPieces: 1,
                courierName: packageData.courier,
                shipmentMethod: "air",
                number: packageData.quantity,
                unit: "lb",
                trackingNumber: packageData.trackingNumber,
                date: formattedDate,
                oversizePackage: false,
                consigneeName: packageData.packageName,
                packageType: "nonperishable",
                deliveryType: "driver",
                _id: undefined,
                deliveryInfo: packageData.deliveryInfo,
                consigneeType: packageData.consigneeType,
            };

            // Create the new package
            const response = await dispatch(handlePostRequest(newPackage, "/addPackage", true, true));

            if (response !== "error") {
                toast.success("Package converted successfully");
                fetchPendingPackages();
            }
        } catch (error) {
            console.error("Conversion error:", error);
            toast.error("Failed to convert package");
        } finally {
            setIsLoading(false);
        }
    };

    const renderActions = (rowData) => (
        <div className={styles.actionButtons}>
            <Tooltip title="Edit" position="top" arrow>
                <Button
                    icon="pi pi-pencil"
                    className="p-button-info"
                    onClick={() => {
                        setEditingPackage(rowData);
                        setIsDialogOpen(true);
                    }}
                />
            </Tooltip>

            <Tooltip title="Delete" position="top" arrow>
                <Button icon="pi pi-trash" className="p-button-danger" onClick={() => handleDeletePackage(rowData)} />
            </Tooltip>

            <Tooltip title="Convert to package" position="top" arrow>
                <Button icon="pi pi-sync" className="p-button-info p-mr-2" onClick={() => handleConvertToPackage(rowData)} />
            </Tooltip>
        </div>
    );

    const handlePackageSubmit = async (values, isEdit) => {
        try {
            setIsLoading(true);
            setIsDialogOpen(false);

            const result = await handlePutRequest(values, "/ocr-packages/update");
            if (result?.success) {
                toast.success("Package updated successfully");
                fetchPendingPackages();
            }
        } catch (error) {
            console.error("Package submission error:", error);
            toast.error(`Error ${isEdit ? "updating" : "adding"} package`);
        } finally {
            setIsLoading(false);
            setEditingPackage(null);
        }
    };

    return (
        <div className={styles.container}>
            <h2>OCR Packages</h2>

            <div className={styles.buttonsWrapper} style={{ gap: "10px" }}>
                <Tooltip title="Add Single" position="top" arrow>
                    <Button icon="pi pi-plus" className="p-button-success p-mr-2" onClick={() => setIsSingleOpen(true)} />
                </Tooltip>

                <Tooltip title="Add multiple" position="top" arrow>
                    <Button icon="pi pi-upload" className="p-button-info p-mr-2" onClick={() => setIsMultipleOpen(true)} />
                </Tooltip>
            </div>

            <Dialog visible={isSingleOpen} onHide={() => setIsSingleOpen(false)} header="Add single package" style={{ width: `${window.innerWidth < 1024 ? "90vw" : "50vw"}` }}>
                <AddSingleDialog
                    onHide={() => setIsSingleOpen(false)}
                    onclose={() => {
                        setIsSingleOpen(false);
                        fetchPendingPackages();
                    }}
                />
            </Dialog>

            <Dialog visible={isMultipleOpen} onHide={() => setIsMultipleOpen(false)} header="Add multiple packages" style={{ width: `${window.innerWidth < 1024 ? "90vw" : "50vw"}` }}>
                <AddMultipleDialog
                    onHide={() => setIsMultipleOpen(false)}
                    onclose={() => {
                        setIsMultipleOpen(false);
                        fetchPendingPackages();
                    }}
                />
            </Dialog>

            <Dialog
                visible={isDialogOpen}
                onHide={() => {
                    setIsDialogOpen(false);
                    setEditingPackage(null);
                }}
                header={editingPackage ? "Edit Package" : "Add Package"}
                style={{ width: `${window.innerWidth < 1024 ? "90vw" : "50vw"}` }}
            >
                <EditOCRPackage onSubmit={handlePackageSubmit} editData={editingPackage} />
            </Dialog>

            {isLoading && (
                <div className={styles.spinnerWrapper}>
                    <ProgressSpinner />
                </div>
            )}

            <div className={styles.tableWrapper}>
                <DataTable
                    value={pendingPackages}
                    paginator
                    rows={100}
                    stripedRows
                    responsiveLayout="scroll"
                    rowsPerPageOptions={[100, 500, 1000]}
                    className="datatable-responsive"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Records"
                    emptyMessage="No packages found"
                    selection={selectedRow}
                    onSelectionChange={(e) => setSelectedRow(e.value)}
                    sortMode="multiple"
                    filterDisplay="row"
                    filterPlaceholder="Search"
                    filterLocale="en"
                >
                    <Column selectionMode="multiple" style={{ width: "3em" }} />
                    <Column header="Actions" body={renderActions} />
                    <Column field="description" header="Description" filter sortable />
                    <Column field="quantity" header="Quantity" filter sortable />
                    <Column field="packageName" header="Package Name" filter sortable />
                    <Column field="consigneeType" header="Client Type" filter sortable />
                    <Column field="courierName" header="Courier" filter sortable />
                    <Column field="billToName" header="Bill To" filter sortable />
                    <Column field="trackingNumber" header="Tracking #" filter sortable />
                </DataTable>
            </div>
        </div>
    );
};

export default OCRPackages;
